<template lang="pug">
  .feed-post-card
    .post-header
      EntityAvatar(:image="avatar" size-class="is-48x48" is-rounded)
      .post-header-meta
        p
          strong.mr-1 {{ authorName }}
          span.action-verb {{ actionVerb }}
        p.post-time {{ postTime }}
      .post-header-right(v-if="!isSiteWide")
        BTooltip.visibility-tooltip(
          v-if="showVisibility && isAlwaysPublicPost"
          :label="alwaysPublicLabel"
          position="is-left"
          type="is-dark"
        )
          WtSvgIcon(icon="eye" width="24px" height="24px")
        BDropdown.visibility-menu(
          v-else-if="showVisibility && showManageActions"
          position="is-bottom-left"
          aria-role="menu"
        )
          a.visibility-trigger(
            slot="trigger"
            role="button"
            aria-label="Post visibility"
          )
            WtSvgIcon(:key="visibilityIcon" :icon="visibilityIcon" width="24px" height="24px")
          BDropdownItem.visibility-option(
            v-for="option in visibilityOptions"
            :key="option.code"
            aria-role="menuitem"
            :class="{ 'is-selected': option.code === visibilityCode }"
            @click="changeVisibility(option.code)"
          )
            span {{ option.label }}
        BTooltip(
          v-else-if="showVisibility && visibilityLabel"
          :label="visibilityLabel"
          position="is-left"
          type="is-dark"
        )
          WtSvgIcon(:key="visibilityIcon" :icon="visibilityIcon" width="24px" height="24px")
        BDropdown.post-menu(
          v-if="showManageActions"
          position="is-bottom-left"
          aria-role="menu"
        )
          a.post-menu-trigger(slot="trigger" role="button" aria-label="Post actions")
            BIcon(icon="dots-horizontal")
          BDropdownItem(
            v-if="isEditable"
            aria-role="menuitem"
            @click="$emit('edit', post)"
          )
            span.has-text-primary.has-text-weight-semibold Edit post
          BDropdownItem.edit-locked(
            v-if="isMetricPost && !isWithinTwoWeeks"
            aria-role="menuitem"
            custom
          )
            .edit-locked-item
              BIcon.edit-locked-icon(icon="lock" size="is-small")
              span Editing unavailable for entries older than 2 weeks
          BDropdownItem(
            aria-role="menuitem"
            @click="$emit('delete', post)"
          )
            span.has-text-danger.has-text-weight-semibold Delete post
    p.post-content(v-if="filteredContent" v-html="filteredContent")
    .metric-row(v-if="metric")
      WtSvgIcon.metric-icon(:icon="metric.icon" width="36px" height="36px")
      .metric-details
        span.metric-label.has-text-weight-semibold {{ metric.label }}
        span.metric-value {{ metric.value }}
    figure.post-image(v-if="postImage")
      img(:src="postImage" alt="Post photo")
    .post-bottom-component
      .reactions-count-display(v-if="post.reactions && post.reactions.total > 0")
        a.reactions-link(@click="$refs.feedPostReactions.openReactionsModal()")
          .emojis-display
            WtSvgIcon(
              v-for="emoji in sortedReactionEmojis"
              :key="emoji"
              :icon="emojiMap[emoji]"
              width="18px"
              height="18px"
            )
          span.count {{ post.reactions.total }}
      .post-actions
        FeedPostReactions(ref="feedPostReactions" :post="post")
        a.post-action.has-text-black(@click="toggleReplies")
          WtSvgIcon.post-action-icon(icon="chat" width="16px" height="16px")
          span {{ replyActionLabel }}
      .post-replies(v-if="showReplies")
        .reply-item(v-for="reply in replies" :key="reply.id")
          EntityAvatar(:image="replyAvatar(reply)" size-class="is-32x32" is-rounded)
          .reply-body
            .reply-header
              strong {{ replyAuthor(reply) }}
              .reply-header-right
                BDropdown.reply-menu(
                  v-if="canDeleteReply(reply)"
                  position="is-bottom-left"
                  aria-role="menu"
                )
                  a.reply-menu-trigger(slot="trigger" role="button" aria-label="Reply actions")
                    BIcon(icon="dots-horizontal" size="is-small")
                  BDropdownItem(
                    aria-role="menuitem"
                    @click="confirmDeleteReply(reply)"
                  )
                    span.has-text-danger.has-text-weight-semibold Delete
                span.reply-time {{ replyTime(reply) }}
            p.reply-text(v-if="filteredReplyText(reply)" v-html="filteredReplyText(reply)")
            figure.reply-image(v-if="reply.image")
              img(:src="reply.image" alt="Reply photo")
        .reply-form
          .reply-form-input-container
            BInput(
              v-model="newReply"
              type="textarea"
              rows="3"
              maxlength="1000"
              placeholder="Write something..."
            )
          WtButton.reply-submit-btn(
            :disabled="!canSubmitReply"
            @click.native="submitReply"
          ) Submit
</template>

<script>
import { mapActions } from "vuex";
import EntityAvatar from "@/components/WtEntityAvatar/WtEntityAvatar";
import WtSvgIcon from "@@/WtSvgIcon/WtSvgIcon";
import WtButton from "@@/WtButton/WtButton";
import FeedPostReactions from "@@/Members/FeedPosts/FeedPostReactions";

const VISIBILITY_OPTIONS = [
  { code: 0, label: "Only Me", icon: "eye-off" },
  { code: 1, label: "Friends", icon: "eye-friends" },
  { code: 2, label: "Anyone", icon: "eye" }
];

const WELLNESS_METRICS = {
  heart: { icon: "heart_rate", label: "Heart rate" },
  sleep: { icon: "sleep", label: "Sleep" },
  weight: { icon: "monitor_weight", label: "Weight" },
  nutrition: { icon: "nutrition", label: "Nutrition" },
  water: { icon: "water", label: "Water" },
  mood: { icon: "mood", label: "Mood" },
  mindfulness: { icon: "mindfulness", label: "Mindfulness" }
};

export default {
  name: "FeedPostCard",

  components: {
    EntityAvatar,
    WtSvgIcon,
    WtButton,
    FeedPostReactions
  },

  props: {
    post: {
      type: Object,
      required: true
    },

    canManage: {
      type: Boolean,
      default: false
    },

    showVisibility: {
      type: Boolean,
      default: true
    },

    isSiteWide: {
      type: Boolean,
      default: false
    }
  },

  data() {
    return {
      showReplies: false,
      newReply: "",
      isSubmittingReply: false,
      showReactionsModal: false,
      detailedReactions: [],
      isLoadingDetailedReactions: false,
      emojiMap: {
        applaud: "applaud",
        happy: "smiley",
        love: "love",
        like: "like",
        good_luck: "luck",
        laugh: "laugh"
      }
    };
  },

  computed: {
    avatar() {
      return this._.get(this.post, "user.data.avatar", "");
    },

    authorName() {
      if (this.post.userId === this.$auth.user().id) {
        return "You";
      }
      return this._.get(this.post, "user.data.name", "");
    },

    showManageActions() {
      return this.canManage && this.post.userId === this.$auth.user().id;
    },

    isWithinTwoWeeks() {
      if (!this.post.postDate) return true;
      const cutoff = this.moment()
        .subtract(14, "days")
        .startOf("day");
      return this.moment(this.post.postDate, "YYYY-MM-DD").isSameOrAfter(
        cutoff
      );
    },

    isMetricPost() {
      const { metricType, metricText } = this.post;
      if (metricType === "steps" || metricType === "activities") return true;
      if (metricType === "wellness")
        return Boolean(WELLNESS_METRICS[metricText]);
      return false;
    },

    // Editing reopens the metric log form; posts synced from devices or
    // unknown metric types fall back to delete-only.
    // Metric posts are only editable within 2 weeks of their post date.
    isEditable() {
      if (this.isMetricPost) return this.isWithinTwoWeeks;
      const { metricType } = this.post;
      return !metricType || metricType === "post";
    },

    visibilityCode() {
      return this._.get(this.post, "visibility.code", null);
    },

    isAlwaysPublicPost() {
      return ["steps", "activities"].includes(this.post.metricType);
    },

    alwaysPublicLabel() {
      return this.post.metricType === "steps"
        ? "Steps are always public"
        : "Activities are always public";
    },

    visibilityOptions() {
      return VISIBILITY_OPTIONS;
    },

    selectedVisibility() {
      return VISIBILITY_OPTIONS.find(
        option => option.code === this.visibilityCode
      );
    },

    visibilityIcon() {
      return this._.get(this.selectedVisibility, "icon", "eye");
    },

    visibilityLabel() {
      return this._.get(this.selectedVisibility, "label", "");
    },

    // show the edit time when the post has been updated
    postTime() {
      const timestamp = this.post.updatedAt || this.post.createdAt;
      return this.moment(timestamp).format("hh:mm A");
    },

    filteredContent() {
      if (!this.post.content) {
        return "";
      }
      return this.sanitize(this.post.content, {
        allowedTags: [],
        allowedAttributes: {}
      });
    },

    postImage() {
      return this.post.largeImage || this.post.image || "";
    },

    actionVerb() {
      switch (this.post.metricType) {
        case "steps":
          return "logged steps";
        case "activities":
          return "logged an activity";
        case "wellness":
          return this.wellnessVerb;
        default:
          return this.postImage ? "added a photo" : "added a post";
      }
    },

    wellnessVerb() {
      switch (this.post.metricText) {
        case "heart":
          return "logged heart rate";
        case "sleep":
          return "logged sleep";
        case "weight":
          return "logged weight";
        case "nutrition":
          return "logged nutrition";
        default:
          return `logged ${this.post.metricText}`;
      }
    },

    metric() {
      const { metricType, metricText } = this.post;

      if (metricType === "steps") {
        return {
          icon: "red-circle-shoe",
          label: "Steps",
          value: `${this.formatNumber(this.post.metricStepValue)} steps`
        };
      }

      if (metricType === "activities") {
        const steps = this.post.metricStepValue
          ? ` (${this.formatNumber(this.post.metricStepValue)} steps)`
          : "";
        return {
          icon: "activities-builder",
          label: this._.startCase(metricText),
          value: `${Math.round(Number(this.post.metricValue))} mins${steps}`
        };
      }

      if (metricType === "wellness" && WELLNESS_METRICS[metricText]) {
        return {
          ...WELLNESS_METRICS[metricText],
          value: this.wellnessValue
        };
      }

      return null;
    },

    wellnessValue() {
      const value = Number(this.post.metricValue);

      switch (this.post.metricText) {
        case "heart":
          return `${Math.round(value)} BPM`;
        case "sleep":
          return this.formatSleep(value);
        case "weight":
          return `${Math.round(value)} ${this.weightUnit}`;
        case "nutrition":
          return this.post.metricSubText || `${Math.round(value)}`;
        default:
          return this.post.metricSubText || `${Math.round(value)}`;
      }
    },

    // backend stores weight in LBS and converts to KG for metric-unit members
    weightUnit() {
      return this._.get(this.$auth.user(), "memberData.units") === "Miles"
        ? "LBS"
        : "KG";
    },

    // Replies are a flat, single-level list (no reply-to-reply chains).
    // Nested replies created by older clients are flattened in for display.
    replies() {
      const topLevel = this._.get(this.post, "comments.data", []);
      const nested = this._.flatMap(topLevel, comment =>
        this._.get(comment, "replies.data", [])
      );

      return [...topLevel, ...nested].sort(
        (a, b) =>
          this.moment(b.created_at).valueOf() -
          this.moment(a.created_at).valueOf()
      );
    },

    replyActionLabel() {
      const count = this.replies.length;

      if (count === 0) {
        return "Reply";
      }
      return count === 1 ? "1 reply" : `${count} replies`;
    },

    canSubmitReply() {
      return this.newReply.trim().length > 0 && !this.isSubmittingReply;
    },

    sortedReactionEmojis() {
      const emojis = this._.get(this.post, "reactions.emojis", []);
      const emojiOrder = [
        "applaud",
        "happy",
        "love",
        "like",
        "good_luck",
        "laugh"
      ];
      return [...emojis].sort(
        (a, b) => emojiOrder.indexOf(a) - emojiOrder.indexOf(b)
      );
    }
  },

  watch: {
    showReactionsModal(newVal) {
      if (newVal && this.post.reactions && this.post.reactions.total > 0) {
        this.fetchDetailedReactions();
      }
    }
  },

  methods: {
    ...mapActions(["snackBar"]),
    ...mapActions("feed", [
      "addFeedPostReply",
      "deleteFeedPostReply",
      "updateFeedPostVisibility"
    ]),

    async fetchDetailedReactions() {
      if (this.isLoadingDetailedReactions || this.post.id === undefined) {
        return;
      }

      this.isLoadingDetailedReactions = true;
      try {
        const response = await this.$http.get(
          `feed/posts/${this.post.id}/reactions`
        );

        let data = this._.get(response, "data.data", []);

        if (!Array.isArray(data) || data.length === 0) {
          data = this._.get(response, "data", []);
        }

        this.detailedReactions = data;
      } catch (error) {
        console.error(
          "FeedPostCard: Error fetching detailed reactions:",
          error
        );
        this.detailedReactions = [];
      } finally {
        this.isLoadingDetailedReactions = false;
      }
    },

    toggleReplies() {
      this.showReplies = !this.showReplies;
    },

    async changeVisibility(visibility) {
      if (visibility === this.visibilityCode) {
        return;
      }

      const response = await this.updateFeedPostVisibility({
        postId: this.post.id,
        visibility
      });

      if (response.error) {
        this.snackBar({
          type: "is-danger",
          duration: 10000,
          queue: false,
          message: this._.get(
            response,
            "error.message",
            "Error updating post visibility."
          )
        });
      }
    },

    replyAvatar(reply) {
      return this._.get(reply, "user.data.avatar", "");
    },

    replyAuthor(reply) {
      return this._.get(reply, "user.data.name", "");
    },

    replyTime(reply) {
      return this.moment(reply.created_at).fromNow();
    },

    filteredReplyText(reply) {
      if (!reply.text) {
        return "";
      }
      return this.sanitize(reply.text, {
        allowedTags: [],
        allowedAttributes: {}
      });
    },

    canDeleteReply(reply) {
      const authUserId = this.$auth.user().id;
      const isCommenter = reply.user_id === authUserId;
      const isPostOwner = this.post.userId === authUserId;
      return isCommenter || isPostOwner;
    },

    async submitReply() {
      if (!this.canSubmitReply) {
        return;
      }

      this.isSubmittingReply = true;
      const response = await this.addFeedPostReply({
        postId: this.post.id,
        text: this.newReply.trim()
      });
      this.isSubmittingReply = false;

      if (response.error) {
        this.snackBar({
          type: "is-danger",
          duration: 10000,
          queue: false,
          message: this._.get(response, "error.message", "Error adding reply.")
        });
        return;
      }

      this.newReply = "";
    },

    confirmDeleteReply(reply) {
      this.$buefy.dialog.confirm({
        title: "Delete reply",
        message:
          "Are you sure you want to delete this reply? This action cannot be undone.",
        confirmText: "Delete reply",
        cancelText: "Cancel",
        type: "is-danger",
        hasIcon: true,
        onConfirm: () => this.handleDeleteReply(reply)
      });
    },

    async handleDeleteReply(reply) {
      const response = await this.deleteFeedPostReply({
        postId: this.post.id,
        replyId: reply.id
      });

      if (response.error) {
        this.snackBar({
          type: "is-danger",
          duration: 10000,
          queue: false,
          message: this._.get(
            response,
            "error.message",
            "Error deleting reply."
          )
        });
        return;
      }

      this.snackBar({ message: "Reply deleted!" });
    },

    formatNumber(value) {
      return Number(value || 0).toLocaleString();
    },

    formatSleep(minutes) {
      const hrs = Math.floor(minutes / 60);
      const mins = Math.round(minutes % 60);

      if (hrs === 0) {
        return `${mins} mins`;
      }
      return `${hrs} hrs ${mins} mins`;
    }
  }
};
</script>

<style lang="sass" scoped>
.feed-post-card
  display: flex
  flex-direction: column
  gap: 8px
  width: 100%
  opacity: 1
  background: $white
  border-radius: 8px
  padding: 0.75rem 1rem
  margin-bottom: 0.75rem
  &:last-child
    margin-bottom: 0
  .post-header
    display: flex
    align-items: flex-start
    .post-header-meta
      flex: 1
      margin-left: 0.5rem
      p
        margin: 0
        line-height: 1.4
        font-size: 16px
        font-weight: 400
        strong
          font-weight: 600
        .action-verb
          color: #5D6472
      .post-time
        font-size: 14px
        font-weight: 400
        color: #5D6472
    .post-header-right
      display: flex
      align-items: center
      gap: 0.5rem
      ::v-deep .dropdown-trigger
        display: flex
        align-items: center
      ::v-deep .WtSvgIcon
        line-height: 0
        svg
          display: block
      .visibility-tooltip
        ::v-deep .tooltip-content
          padding: 8px 12px
          border-radius: 8px
          font-size: 13px
          line-height: 1.4
      .visibility-trigger
        display: flex
        align-items: center
        padding: 0 0.25rem
      .visibility-menu
        ::v-deep .dropdown-content
          border-radius: 12px
          box-shadow: 0 4px 16px rgba(0, 0, 0, 0.15)
          padding: 0.5rem
        ::v-deep .dropdown-item
          border-radius: 8px
          text-align: center
          color: #2972F0
          font-size: 14px
          font-weight: 400
          padding: 0.5rem 1.5rem
          &.is-selected
            background: #C6DCFF
      .post-menu-trigger
        display: flex
        align-items: center
        padding: 0 0.25rem
        color: #6E7074
        &:hover
          color: $grey-darker
      .post-menu
        ::v-deep .dropdown-content
          border-radius: 8px
          box-shadow: 0 4px 16px rgba(0, 0, 0, 0.15)
        .edit-locked-item
          display: flex
          align-items: center
          gap: 0.35rem
          color: #9EA3AC
          font-size: 14px
          cursor: default
  .post-content
    margin: 0
    word-break: break-word
    font-size: 16px
    font-weight: 400
  .metric-row
    display: flex
    align-items: center
    background: $white-ter
    border-radius: 8px
    padding: 0.75rem
    .metric-icon
      flex-shrink: 0
      margin-right: 0.75rem
    .metric-details
      display: flex
      flex-direction: column
      .metric-label
        font-size: 16px
        font-weight: 600
        line-height: 1.4
        color: #1B1D20
      .metric-value
        font-size: 13px
        font-weight: 400
        line-height: 1.4
        color: #5D6472
  .post-image
    img
      border-radius: 8px
      width: 100%
      display: block
  .reactions-count-display
    padding: 8px 0
    border-bottom: 1px solid #E8EBEF

    .reactions-link
      display: inline-flex
      align-items: center
      gap: 8px
      cursor: pointer
      user-select: none
      transition: opacity 0.2s ease
      border-radius: 16px
      background-color: #F0F2F5
      padding: 4px 10px

      &:hover
        opacity: 0.75

      .emojis-display
        display: flex
        gap: 4px

        span
          font-size: 16px

      .count
        font-size: 14px
        font-weight: 600
        color: #5D6472

  .post-actions
    display: flex
    align-items: center
    border-top: 1px solid $grey-lighter
    padding-top: 0.5rem
    margin-left: 15px

    ::v-deep .feed-post-reactions
      margin-right: 20px
    .post-action
      display: flex
      align-items: center
      font-size: 14px
      font-weight: 400
      .post-action-icon
        margin-right: 0.35rem
  .post-replies
    display: flex
    flex-direction: column
    gap: 12px
    margin-top: 8px
    .reply-item
      display: flex
      align-items: flex-start
      gap: 0.5rem
      .reply-body
        flex: 1
        .reply-header
          display: flex
          align-items: center
          justify-content: space-between
          margin-bottom: 4px
          strong
            line-height: 1.4
            font-size: 16px
            font-weight: 600
            color: #1B1D20
          .reply-header-right
            display: flex
            align-items: center
            gap: 0.5rem
            .reply-menu-trigger
              display: flex
              align-items: center
              padding: 0 0.25rem
              color: #6E7074
              &:hover
                color: $grey-darker
            .reply-menu
              ::v-deep .dropdown-content
                border-radius: 8px
                box-shadow: 0 4px 16px rgba(0, 0, 0, 0.15)
            .reply-time
              font-size: 13px
              font-weight: 400
              color: #5D6472
        .reply-text
          margin: 0
          word-break: break-word
          color: #1B1D20
          font-weight: 400
          font-size: 15px
          line-height: 1.5
        .reply-image
          margin-top: 0.5rem
          img
            border-radius: 8px
            max-width: 240px
            display: block
    .reply-form
      display: flex
      flex-direction: column
      gap: 4px
      margin-top: 16px
      .reply-form-input-container
        width: 100%
        ::v-deep textarea
          width: 100%
          border: 1px solid #E8EBEF
          border-radius: 12px
          padding: 12px 16px
          font-size: 15px
          line-height: 1.5
          resize: none
          min-height: 60px
          &:focus
            border-color: #2972F0
            box-shadow: none
      .reply-submit-btn
        align-self: flex-end
        background-color: #2972F0
        color: #FFFFFF
        border-radius: 20px
        font-size: 14px
        font-weight: 600
        height: 36px
        padding: 0 20px
        border: none
        &:hover
          background-color: #1E5BD0
        &:disabled
          opacity: 0.5
          cursor: not-allowed
          background-color: #2972F0

.modal-card.reactions-modal
  .modal-card-head
    background: white
    border-bottom: 1px solid #E8EBEF

    .modal-card-title
      font-weight: 600
      color: #1B1D20

  .modal-card-body
    padding: 0
    background: white

    .reactions-list
      display: flex
      flex-direction: column
      max-height: 500px
      overflow-y: auto

      .reaction-row
        display: grid
        grid-template-columns: 50px 1fr 50px
        gap: 12px
        align-items: center
        padding: 12px 16px
        border-bottom: 1px solid #F0F2F5
        transition: background 0.2s ease

        &:last-child
          border-bottom: none

        &:hover
          background: #F8F9FA

        .reaction-avatar
          display: flex
          align-items: center
          justify-content: center

          .avatar-placeholder
            display: flex
            align-items: center
            justify-content: center
            width: 32px
            height: 32px
            border-radius: 50%
            background: linear-gradient(135deg, #2972F0 0%, #1E5BD0 100%)
            color: white
            font-weight: 600
            font-size: 14px

        .reaction-user
          font-size: 14px
          color: #1B1D20
          font-weight: 500
          word-break: break-word

        .reaction-emoji
          display: flex
          align-items: center
          justify-content: center
          font-size: 20px

    .loading-state
      display: flex
      align-items: center
      justify-content: center
      min-height: 200px
      color: #8A8E96

    .empty-state
      display: flex
      align-items: center
      justify-content: center
      min-height: 200px
      color: #8A8E96
      font-size: 14px
</style>
